import React from 'react';
import { FaCheck } from 'react-icons/fa';
import { RxCross2 } from 'react-icons/rx';

interface PasswordValidationProps {
    password: string;
    passwordConfirmation: string;
    checkPasswordValidity: (isValid: boolean) => void;
}

const computeErrors = (password: string, passwordConfirmation: string) => {
    if (!password) {
        // if password is empty, all the errors will be true;
        return {
            passwordLength: true,
            containsNumber: true,
            containsUpperCaseLetter: true,
            containsLowerCaseLetter: true,
            containsSymbols: true,
            passwordMatches: true,
        };
    }

    const freshErrors = {
        passwordLength: false,
        containsNumber: false,
        containsUpperCaseLetter: false,
        containsLowerCaseLetter: false,
        containsSymbols: false,
        passwordMatches: false,
    };

    if (password?.length < 12) {
        freshErrors.passwordLength = true;
    }

    const r = /\d/;
    //Contain Numbers
    if (!r.test(password)) {
        freshErrors.containsNumber = true;
    }

    //Is Upper Case
    const upperCaseLetters = /[A-Z]/g;
    if (!upperCaseLetters.test(password)) {
        freshErrors.containsUpperCaseLetter = true;
    }

    //Is Lower Case
    const lowerCaseLetters = /[a-z]/g;
    if (!lowerCaseLetters.test(password)) {
        freshErrors.containsLowerCaseLetter = true;
    }

    //Contain Symbols
    // const Symbols = /[!@#$%^&*(),.?":{}|<>`¬_£+_-]/g;
    const Symbols = /[.!@#~$%^&*()+|_]/g;
    if (!Symbols.test(password)) {
        freshErrors.containsSymbols = true;
    }

    // validate if the password matches
    // Matching passwords
    if (password && password != passwordConfirmation) {
        freshErrors.passwordMatches = true;
    }

    return freshErrors;
};

const PasswordValidationRules = ({
    password,
    passwordConfirmation,
    checkPasswordValidity,
}: PasswordValidationProps) => {
    const errors = computeErrors(password, passwordConfirmation);

    // Used to avoid form submit if password doesn't match criterias
    React.useEffect(() => {
        const isValid = Object.values(errors).every((item) => !item);
        checkPasswordValidity(isValid);
    }, [password, passwordConfirmation, checkPasswordValidity, errors]);

    return (
        <div className="form-messages-wrapper">
            <div className="form-message">
                {errors.passwordLength ? (
                    <RxCross2 className="icon-cross" />
                ) : (
                    <FaCheck className="icon-check" />
                )}
                <span className={`input-${errors.passwordLength ? 'error-' : ''}message`}>
                    At least 12 characters in length
                </span>
            </div>

            <div className="form-message last">
                {errors.containsNumber ? (
                    <RxCross2 className="icon-cross" />
                ) : (
                    <FaCheck className="icon-check" />
                )}
                <span className={`input-${errors.containsNumber ? 'error-' : ''}message`}>
                    Contains a number (0-9)
                </span>
            </div>

            <div className="form-message">
                {errors.containsUpperCaseLetter ? (
                    <RxCross2 className="icon-cross" />
                ) : (
                    <FaCheck className="icon-check" />
                )}
                <span className={`input-${errors.containsUpperCaseLetter ? 'error-' : ''}message`}>
                    Contains an uppercase letter (A-Z)
                </span>
            </div>

            <div className="form-message last">
                {errors.containsSymbols ? (
                    <RxCross2 className="icon-cross" />
                ) : (
                    <FaCheck className="icon-check" />
                )}
                <span className={`input-${errors.containsSymbols ? 'error-' : ''}message`}>
                    Contains a special character
                </span>
            </div>

            <div className="form-message">
                {errors.containsLowerCaseLetter ? (
                    <RxCross2 className="icon-cross" />
                ) : (
                    <FaCheck className="icon-check" />
                )}
                <span className={`input-${errors.containsLowerCaseLetter ? 'error-' : ''}message`}>
                    Contains a lowercase letter (a-z)
                </span>
            </div>

            <div className="form-message last">
                {errors.passwordMatches ? (
                    <RxCross2 className="icon-cross" />
                ) : (
                    <FaCheck className="icon-check" />
                )}
                <span className={`input-${errors.passwordMatches ? 'error-' : ''}message`}>
                    The passwords match
                </span>
            </div>
        </div>
    );
};

export default PasswordValidationRules;
